import {
  Box,
  MenuItem,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Loader } from "../../../../Shared/Common/Loader";
import NoRecord from "../../../../Shared/Common/NoRecord";
import usePagination from "../../../../Shared/hooks/usePagination";
import { ArrowDown, ArrowUp } from "../../../../Shared/Icons";
import CustomPagination from "../../../../Shared/JsxPagination";
import { StringRecord } from "../../../../Shared/Type";
import Row from "./Row";
import useLang from "Shared/hooks/useLanguage";
import useIsMobile from "Shared/hooks/useIsMobile";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";
import {
  BulkCompleteNgsRun,
  DeleteOrRejectRunCreation,
  GetAllRunCreation,
} from "Services/NGSRunCreation";
import { StyledDropButton, StyledDropMenu } from "Utils/Style/Dropdownstyle";
import ArrowBottomIcon from "Shared/SVG/ArrowBottomIcon";

export interface SortingTypeI {
  sortingOrder: "asc" | "desc";
  clickedIconData: string | null;
}

export const sortById: SortingTypeI = {
  clickedIconData: "id",
  sortingOrder: "desc",
};
const OpenRuns = () => {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const searchRef = useRef<any>(null);

  const queryDisplayTagNames: StringRecord = {
    runName: "Run Name",
    runDate: "Run Date  ",
    createdName: "Created By",
    runId: "Run Id",
    projectId: "Project Id",
  };

  const initialSearchQuery = {
    id: 0,
    runId: "",
    runName: "",
    runDate: null,
    createdBy: "",
    createdName: "",
    runStatus: "",
    projectId: "",
    downloadStartTime: null,
    downloadEndTime: null,
    isCompleted: false,
    isRejected: false,
    isDeleted: false,
  };

  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [firstRender, setFirstRender] = useState(true);
  const [sort, setSorting] = useState<SortingTypeI>(sortById);
  const [searchedTags, setSearchedTags] = useState<string[]>([]);
  const [searchRequest, setSearchRequest] = useState(initialSearchQuery);
  const [triggerSearchData, setTriggerSearchData] = useState<boolean>(false);
  const [selectedBox, setSelectedBox] = useState({
    ids: [] as number[],
  });
  const [isAddButtonDisabled, setIsAddButtonDisabled] =
    useState<boolean>(false);

  const [anchorEl, setAnchorEl] = React.useState({
    dropdown1: null,
    dropdown2: null,
    dropdown3: null,
    dropdown4: null,
  });
  const openDrop =
    Boolean(anchorEl.dropdown1) ||
    Boolean(anchorEl.dropdown2) ||
    Boolean(anchorEl.dropdown3) ||
    Boolean(anchorEl.dropdown4);

  const handleClick = (event: any, dropdownName: any) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: event.currentTarget });
  };

  // Code for Bulk action for Pending
  const handleClose1 = (dropdownName: string) => {
    setAnchorEl({ ...anchorEl, [dropdownName]: null });
  };

  const {
    total,
    curPage,
    showPage,
    nextPage,
    prevPage,
    setTotal,
    pageSize,
    totalPages,
    pageNumbers,
    setPageSize,
    setCurPage,
  } = usePagination();

  const handleSort = (columnName: any) => {
    searchRef.current.id = searchRef.current.id
      ? searchRef.current.id === "asc"
        ? (searchRef.current.id = "desc")
        : (searchRef.current.id = "asc")
      : (searchRef.current.id = "asc");

    setSorting({
      sortingOrder: searchRef?.current?.id,
      clickedIconData: columnName,
    });

    loadGridData();
  };

  const loadGridData = async (reset: boolean = false) => {
    try {
      setIsLoading(true);
      setIsAddButtonDisabled(true);

      const trimmedSearchRequest = Object.fromEntries(
        Object.entries(searchRequest).map(([key, value]) => [
          key,
          typeof value === "string" ? value.trim() : value,
        ])
      );

      const payload = {
        pageNumber: curPage,
        pageSize: pageSize,
        sortColumn: sort.clickedIconData || "id",
        sortDirection: sort.sortingOrder || "Desc",
        queryModel: reset ? initialSearchQuery : trimmedSearchRequest,
      };
      const res = await GetAllRunCreation(payload);
      setRows(res?.data?.data);
      setTotal(res?.data?.total);
    } catch (error) {
      console.error(
        "An error occurred while fetching Blood Tests setup:",
        error
      );
    } finally {
      setIsLoading(false);
      setIsAddButtonDisabled(false);
    }
  };

  const onInputChangeSearch = (e: any) => {
    setSearchRequest({ ...searchRequest, [e.target.name]: e.target.value });
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setCurPage(1);
      setTriggerSearchData((prev: any) => !prev);
    }
  };

  const handleAllSelect = (checked: boolean, rows: any[]) => {
    setSelectedBox((prev: typeof selectedBox) => ({
      ...prev,
      ids: checked ? rows.map(({ id }) => id) : [],
    }));
  };

  const handleReset = () => {
    setSearchRequest(initialSearchQuery);
    setSorting(sortById);
    loadGridData(true);
    setPageSize(50);
    setIsAddButtonDisabled(false);
    setSelectedBox({ ids: [] });
  };

  const handleTagRemoval = (clickedTag: string) => {
    setSearchRequest((searchRequest) => {
      return {
        ...searchRequest,
        [clickedTag]: (initialSearchQuery as any)[clickedTag],
      };
    });
  };

  const handleDeleteOrReject = async (action: string, runIds: number[]) => {
    if (runIds.length === 0) {
      toast.error(`Please select at least one run to ${action}.`);
      return;
    }
    const payload = {
      action: action,
      runIds: runIds,
    };

    const res = await DeleteOrRejectRunCreation(payload);
    if (res?.data?.statusCode === 200) {
      loadGridData();
      toast.success(res?.data?.message);
      setSelectedBox({ ids: [] });
    } else {
      toast.error(res?.data?.message || "Something went wrong.");
    }
  };

  const handleComplete = async (runIds: number[]) => {
    if (runIds.length === 0) {
      toast.error("Please select at least one run to complete.");
      return;
    }
    const res = await BulkCompleteNgsRun(runIds);
    if (res?.data?.statusCode === 200) {
      loadGridData();
      toast.success(res?.data?.message);
      setSelectedBox({ ids: [] });
    } else {
      toast.error(res?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    const uniqueKeys = new Set<string>();
    for (const [key, value] of Object.entries(searchRequest)) {
      if (value) {
        uniqueKeys.add(key);
      }
    }
    setSearchedTags(Array.from(uniqueKeys));
  }, [searchRequest]);

  useEffect(() => {
    if (searchedTags.length === 0) handleReset();
  }, [searchedTags.length]);

  useEffect(() => {
    if (firstRender) {
      setFirstRender(false);
    } else {
      loadGridData();
    }
  }, [curPage, pageSize, triggerSearchData]);

  return (
    <>
      <div className="d-flex gap-4 flex-wrap mb-2">
        {searchedTags.map((tag) =>
          tag === "groupId" || tag === "displayType" ? null : (
            <div
              className="d-flex align-items-center cursor-pointer gap-1 p-2 rounded bg-light"
              onClick={() => handleTagRemoval(tag)}
              key={tag}
            >
              <span className="fw-bold">{t(queryDisplayTagNames[tag])}</span>
              <i className="bi bi-x"></i>
            </div>
          )
        )}
      </div>
      <div className="d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center responsive-flexed-actions mb-2 gap-2">
        <div className="d-flex align-items-center responsive-flexed-actions gap-2">
          <div className="d-flex align-items-center">
            <span className="fw-400 mr-3">{t("Records")}</span>
            <select
              id={`NGSCompendiumrecords`}
              className="form-select w-100px h-33px rounded"
              data-allow-clear="true"
              data-dropdown-parent="#kt_menu_63b2e70320b73"
              data-kt-select2="true"
              data-placeholder={t("Select option")}
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value))}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="d-flex align-items-center gap-2">
            <PermissionComponent
              moduleName="NGS LIS"
              pageName="View Run"
              permissionIdentifier="AddNewRun"
            >
              {({ pageName, moduleName, permissionIdentifier }) => (
                <button
                  id={`NGSCompendiumAddNew`}
                  className="btn btn-primary btn-sm btn-primary--icon px-7"
                  disabled={isAddButtonDisabled}
                  onClick={() => navigate("/ngs-create-run")}
                >
                  <i className="fa" style={{ fontSize: 11 }}>
                    
                  </i>
                  {getPermissionDisplayName(
                    pageName,
                    moduleName,
                    permissionIdentifier,
                    "Add New"
                  )}
                </button>
              )}
            </PermissionComponent>
          </div>
          <div className="d-flex align-items-center gap-2">
            <StyledDropButton
              id={`BloodResultDataBulkAction`}
              aria-controls={openDrop ? "demo-positioned-menu1" : undefined}
              aria-haspopup="true"
              aria-expanded={openDrop ? "true" : undefined}
              onClick={(event: any) => handleClick(event, "dropdown1")}
              className="btn btn-info btn-sm"
            >
              {t("Bulk Action")}
              <span className="svg-icon svg-icon-5 m-0">
                <ArrowBottomIcon />
              </span>
            </StyledDropButton>
            <StyledDropMenu
              id={`BloodResultDataBulkActionMenu`}
              aria-labelledby="demo-positioned-button1"
              anchorEl={anchorEl.dropdown1}
              open={Boolean(anchorEl.dropdown1)}
              onClose={() => handleClose1("dropdown1")}
              sx={{
                padding: 0,
              }}
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              {/* <PermissionComponent
                moduleName="NGS LIS"
                pageName="View Run"
                permissionIdentifier="BulkComplete"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <MenuItem className=" p-0">
                    <a
                      id={`BloodResultDataBulkReport`}
                      className="w-100 text-dark"
                      onClick={() => {
                        handleClose1("dropdown1");
                        handleComplete(selectedBox.ids);
                      }}
                    >
                      {getPermissionDisplayName(
                        pageName,
                        moduleName,
                        permissionIdentifier,
                        "Complete"
                      )}
                    </a>
                  </MenuItem>
                )}
              </PermissionComponent> */}
              <PermissionComponent
                moduleName="NGS LIS"
                pageName="View Run"
                permissionIdentifier="BulkDelete"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <MenuItem className=" p-0">
                    <a
                      id={`BloodResultDataBulkReport`}
                      className="w-100 text-dark"
                      onClick={() => {
                        handleClose1("dropdown1");
                        handleDeleteOrReject("delete", selectedBox.ids);
                      }}
                    >
                      {getPermissionDisplayName(
                        pageName,
                        moduleName,
                        permissionIdentifier,
                        "Delete"
                      )}
                    </a>
                  </MenuItem>
                )}
              </PermissionComponent>
              <PermissionComponent
                moduleName="NGS LIS"
                pageName="View Run"
                permissionIdentifier="BulkReject"
              >
                {({ pageName, moduleName, permissionIdentifier }) => (
                  <MenuItem className=" p-0">
                    <a
                      id={`BloodResultDataBulkReport`}
                      className="w-100 text-dark"
                      onClick={() => {
                        handleClose1("dropdown2");
                        handleDeleteOrReject("reject", selectedBox.ids);
                      }}
                    >
                      {getPermissionDisplayName(
                        pageName,
                        moduleName,
                        permissionIdentifier,
                        "Reject"
                      )}
                    </a>
                  </MenuItem>
                )}
              </PermissionComponent>
            </StyledDropMenu>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            id={`NGSCompendiumSearch`}
            className="btn btn-linkedin btn-sm fw-500"
            aria-controls="Search"
            onClick={() => {
              setCurPage(1);
              setTriggerSearchData((prev: any) => !prev);
            }}
          >
            {t("Search")}
          </button>
          <button
            className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
            id={`NGSCompendiumReset`}
            type="button"
            onClick={handleReset}
          >
            <span>{t("Reset")}</span>
          </button>
        </div>
      </div>
      <div className="card">
        <Box sx={{ height: "auto", width: "100%" }}>
          <div className="table_bordered overflow-hidden">
            <TableContainer
              sx={
                isMobile
                  ? {}
                  : {
                      maxHeight: "calc(100vh - 100px)",
                      "&::-webkit-scrollbar": {
                        width: 7,
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#fff",
                      },
                      "&:hover": {
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "var(--kt-gray-400)",
                          borderRadius: 2,
                        },
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "var(--kt-gray-400)",
                        borderRadius: 2,
                      },
                    }
              }
              component={Paper}
              className="shadow-none"
            >
              <Table
                aria-label="sticky table collapsible"
                className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                <TableHead>
                  <TableRow className="h-40px">
                    <TableCell className="w-30px"></TableCell>
                    <TableCell className="w-30px"></TableCell>
                    <TableCell className="w-50px"></TableCell>
                    <TableCell className="w-300px">
                      <input
                        id={`NGSRunCreationRunName`}
                        type="text"
                        name="runName"
                        className="form-control bg-white rounded-2 fs-8 h-30px"
                        placeholder={t("Search ....")}
                        value={searchRequest.runName}
                        onChange={onInputChangeSearch}
                        onKeyDown={(e) => handleKeyPress(e)}
                      />
                    </TableCell>
                    <TableCell className="w-300px">
                      <SearchDatePicker
                        name="runDate"
                        value={searchRequest.runDate}
                        onChange={onInputChangeSearch}
                      />
                    </TableCell>
                    <TableCell className="w-300px">
                      <input
                        id={`NGSRunCreationCreatedBy`}
                        type="text"
                        name="createdName"
                        className="form-control bg-white rounded-2 fs-8 h-30px"
                        placeholder={t("Search ....")}
                        value={searchRequest.createdName}
                        onChange={onInputChangeSearch}
                        onKeyDown={(e) => handleKeyPress(e)}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow className="h-30px">
                    <TableCell></TableCell>
                    <TableCell className="w-20px min-w-20px">
                      <label className="form-check form-check-sm form-check-solid d-flex justify-content-center">
                        <input
                          id={`ManageOrderNewOrderCheckAll`}
                          className="form-check-input"
                          type="checkbox"
                          onChange={(e) =>
                            handleAllSelect(e.target.checked, rows)
                          }
                          checked={
                            selectedBox.ids.length === rows.length &&
                            rows.length > 0
                          }
                        />
                      </label>
                    </TableCell>
                    <TableCell>{t("Actions")}</TableCell>
                    <TableCell>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("runName")}
                        ref={searchRef}
                      >
                        <div style={{ width: "max-content" }}>
                          {t("Run Name")}
                        </div>

                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "runName"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "runName"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell sx={{ width: "max-content" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("runDate")}
                        ref={searchRef}
                      >
                        <div style={{ width: "max-content" }}>
                          {t("Run Date")}
                        </div>
                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "runDate"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "runDate"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell sx={{ width: "max-content" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("createdName")}
                        ref={searchRef}
                      >
                        <div style={{ width: "max-content" }}>
                          {t("Created By")}
                        </div>
                        <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                          <ArrowUp
                            CustomeClass={`${
                              sort.sortingOrder === "desc" &&
                              sort.clickedIconData === "createdName"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                          <ArrowDown
                            CustomeClass={`${
                              sort.sortingOrder === "asc" &&
                              sort.clickedIconData === "createdName"
                                ? "text-success fs-7"
                                : "text-gray-700 fs-7"
                            } p-0 m-0`}
                          />
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading ? (
                    <TableCell colSpan={6}>
                      <Loader />
                    </TableCell>
                  ) : rows?.length ? (
                    rows.map((row: any, index: number) => (
                      <Row
                        row={row}
                        rows={rows}
                        key={row.id}
                        index={index}
                        setRows={setRows}
                        selectedBox={selectedBox}
                        loadGridData={loadGridData}
                        handleComplete={handleComplete}
                        setSelectedBox={setSelectedBox}
                        handleDeleteOrReject={handleDeleteOrReject}
                        queryDisplayTagNames={queryDisplayTagNames}
                        setIsAddButtonDisabled={setIsAddButtonDisabled}
                      />
                    ))
                  ) : (
                    <NoRecord colSpan={6} />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </div>
      <CustomPagination
        curPage={curPage}
        nextPage={nextPage}
        pageNumbers={pageNumbers}
        pageSize={pageSize}
        prevPage={prevPage}
        showPage={showPage}
        total={total}
        totalPages={totalPages}
      />
    </>
  );
};

export default OpenRuns;
